import type { Promisable } from '@eturi/ts-helpers'
import { timeoutPromise } from '@eturi/util'
import type { Location } from 'history'
import type { match } from 'react-router-dom'
import type { AppStore } from '../types'

export type OPResolver = (
	store: AppStore,
	location: Location,
	match: Maybe<match>,
) => Promisable<void>

/** Takes in functions and returns a function that resolves them serially */
export const resolveSerial = <T extends OPResolver[]>(...args: T) =>
	(async (store, location, match) => {
		// Wait at least one tick for other history behavior to finish. This might
		// be in `useBackButton`, for example, where we have to pop and replace
		// some records when the user navigates so the history doesn't get screwy.
		await timeoutPromise(0)
		for (const fn of args) await fn(store, location, match)
	}) satisfies OPResolver

/** Takes in functions and returns a function that resolves them in parallel */
export const resolveParallel = <T extends OPResolver[]>(...fns: T) =>
	(async (store, location, match) => {
		// See resolveSerial
		await timeoutPromise(0)
		await Promise.all(fns.map(async (f) => f(store, location, match)))
	}) satisfies OPResolver

export type ResolverRedirect = {
	readonly type: typeof RESOLVER_REDIRECT_TOKEN
	readonly path: string
}

export const RESOLVER_REDIRECT_TOKEN = '__Resolver_Redirect__'

export const createResolverRedirect = (path: string): ResolverRedirect => ({
	type: RESOLVER_REDIRECT_TOKEN,
	path,
})

export const isResolverRedirect = (err: any): err is ResolverRedirect =>
	err?.type === RESOLVER_REDIRECT_TOKEN
